import React, { useEffect, useMemo, useState } from "react";
import { getOfficialRequirementText } from "../../data/defensieStandards";
import {
  trackPreparationCheckLead,
  trackPreparationCheckView,
} from "../../utils/tracking";

const PREPARATION_CHECK_ENDPOINT =
  process.env.REACT_APP_PREPARATION_CHECK_ENDPOINT ||
  "https://preparationcheck-5xcspkpkvq-uc.a.run.app";

const questions = [
  {
    id: "direction",
    question: "Waar bereid jij je op voor?",
    options: [
      { value: "A", label: "Defensie algemeen", score: 0 },
      {
        value: "B",
        label:
          "Specialistische militaire richting zoals Mariniers, KCT of Luchtmobiel",
        score: 0,
      },
      {
        value: "C",
        label: "Veiligheidsdiensten zoals politie, brandweer of KMar",
        score: 0,
      },
    ],
  },
  {
    id: "loadCarriage",
    question: "Train je momenteel met bepakking?",
    options: [
      { value: "A", label: "Nee, nog niet structureel", score: 0 },
      { value: "B", label: "Ja, licht tot matig: 5-10 km met 10-20 kg", score: 1 },
      {
        value: "C",
        label: "Ja, structureel: 10+ km met 20+ kg en zonder klachten",
        score: 2,
      },
    ],
  },
  {
    id: "pullups",
    question: "Hoeveel strikte pull-ups kun je momenteel uitvoeren?",
    options: [
      { value: "A", label: "0", score: 0 },
      { value: "B", label: "1-4", score: 1 },
      { value: "C", label: "5+", score: 2 },
    ],
  },
  {
    id: "pain",
    question:
      "Heb je momenteel pijn, blessure of iets dat niet lekker voelt tijdens training?",
    options: [
      { value: "A", label: "Ja, dit beperkt mijn training", score: 0 },
      { value: "B", label: "Soms, maar ik train eromheen", score: 1 },
      { value: "C", label: "Nee, ik train zonder klachten", score: 2 },
    ],
  },
  {
    id: "run12",
    question: "Hoeveel meter loop je momenteel in 12 minuten?",
    options: [
      { value: "A", label: "Minder dan 2400 meter", score: 0 },
      { value: "B", label: "2400-2700 meter", score: 1 },
      { value: "C", label: "2700 meter of meer", score: 2 },
    ],
  },
  {
    id: "recovery",
    question: "Hoe herstel je momenteel van je trainingen?",
    options: [
      { value: "A", label: "Slecht: vaak moe, stijf of pijntjes", score: 0 },
      { value: "B", label: "Wisselend: soms goed, soms niet", score: 1 },
      {
        value: "C",
        label: "Goed: ik kan consistent trainen en herstellen",
        score: 2,
      },
    ],
  },
  {
    id: "trainingStructure",
    question: "Train je met een progressief schema dat past bij jouw doel?",
    options: [
      { value: "A", label: "Nee, ik train vooral op gevoel", score: 0 },
      {
        value: "B",
        label: "Ik heb een schema, maar niet specifiek voor mijn doel",
        score: 1,
      },
      { value: "C", label: "Ja, mijn training is doelgericht opgebouwd", score: 2 },
    ],
  },
  {
    id: "strengthTraining",
    question: "Hoe vaak doe je serieuze krachttraining per week?",
    options: [
      { value: "A", label: "0-1 keer", score: 0 },
      { value: "B", label: "2 keer", score: 1 },
      { value: "C", label: "3 keer of vaker", score: 2 },
    ],
  },
  {
    id: "swimming",
    question: "Train je zwemmen of waterconfidence?",
    options: [
      { value: "A", label: "Nee", score: 0 },
      { value: "B", label: "Af en toe", score: 1 },
      { value: "C", label: "Ja, structureel", score: 2 },
    ],
  },
];

const domains = [
  {
    id: "physicalBase",
    title: "Fysieke basis",
    questionIds: ["pullups", "run12", "strengthTraining"],
    text: "Je antwoorden laten zien dat je fysieke basis waarschijnlijk nog meer marge nodig heeft. Denk aan hardlopen, trekkracht en structurele krachttraining. De ondergrens halen is iets anders dan ruim genoeg starten.",
  },
  {
    id: "durability",
    title: "Belastbaarheid",
    questionIds: ["loadCarriage", "pain", "recovery"],
    text: "Je antwoorden wijzen erop dat je lichaam mogelijk nog weinig gewend is aan herhaalde belasting. Bepakking, herstel, pijnsignalen en langere inspanning bepalen of je lichaam belasting kan blijven verwerken.",
  },
  {
    id: "structure",
    title: "Trainingsstructuur",
    questionIds: ["trainingStructure", "recovery"],
    text: "Je voorbereiding lijkt mogelijk nog te weinig gestuurd. Zonder progressief schema is het lastig om te weten of je echt beter wordt of vooral losse trainingen stapelt.",
  },
  {
    id: "specificPreparation",
    title: "Richting-specifieke voorbereiding",
    questionIds: ["loadCarriage", "swimming"],
    text: "Je voorbereiding moet aansluiten op wat jouw richting vraagt. Defensie, specialistische eenheden en veiligheidsdiensten vragen niet precies hetzelfde. Fit zijn is mooi, maar fit waarvoor?",
  },
];

const resultContent = {
  low: {
    title: "Je voorbereiding mist waarschijnlijk nog basis en structuur.",
    text: "Je antwoorden laten zien dat meerdere onderdelen nog aandacht vragen. Dat is geen afkeuring, maar informatie. Je startpunt bepaalt je route. Eerst duidelijk krijgen waar je staat, daarna gericht bouwen aan fysieke basis, belastbaarheid en structuur.",
  },
  mid: {
    title: "Je hebt een begin gemaakt, maar je mist waarschijnlijk nog marge.",
    text: "Je antwoorden laten zien dat er al onderdelen aanwezig zijn, maar dat je voorbereiding waarschijnlijk nog niet overal ruim genoeg is. Voorbereiding draait niet alleen om de ondergrens halen, maar om genoeg marge opbouwen om belasting te kunnen blijven verwerken.",
  },
  highGeneral: {
    title: "Je hebt al een sterke basis. Nu gaat het om specificiteit.",
    text: "Je antwoorden laten zien dat je al serieus bezig bent met belangrijke onderdelen zoals hardlopen, krachttraining, herstel en trainingsstructuur. Dat is een sterke basis. De volgende stap is zorgen dat je voorbereiding blijft aansluiten op jouw richting en dat je deze basis kunt blijven leveren onder vermoeidheid, herhaling en minder ideale omstandigheden.",
  },
  highSpecialist: {
    title: "Je basis lijkt sterk. Nu gaat het om marge en specificiteit.",
    text: "Je antwoorden laten zien dat je al serieus bezig bent met onderdelen zoals hardlopen, krachttraining, bepakking, herstel en richting-specifieke voorbereiding. Dat is sterk. Voor een specialistische militaire richting betekent dit niet dat je klaar bent. Het betekent dat je waarschijnlijk een goede basis hebt om specifieker te bouwen. De volgende stap zit in meer marge: hoger dan minimale eisen, beter herstellen tussen zware dagen, functioneren onder vermoeidheid, bewegen met bepakking en blijven leveren wanneer omstandigheden minder ideaal worden.",
  },
  highSafety: {
    title: "Je basis lijkt sterk voor verdere voorbereiding.",
    text: "Je antwoorden laten zien dat je structureel traint, goed herstelt en geen duidelijke pijnsignalen aangeeft. Voor veiligheidsdiensten is de volgende stap vooral zorgen dat je kracht, conditie en belastbaarheid kunt toepassen in onregelmatige, praktische en soms onverwachte situaties.",
  },
};

const highScoreFocusAreas = [
  {
    id: "specificity",
    title: "Specificiteit",
    text: "Je basis lijkt goed, maar voorbereiding moet blijven aansluiten op wat jouw richting vraagt. De vraag wordt nu minder of je losse onderdelen kunt halen, en meer of je ze kunt blijven leveren onder vermoeidheid, herhaling en druk.",
    score: null,
    maxScore: null,
  },
  {
    id: "repeatedDurability",
    title: "Belastbaarheid",
    text: "Sterke scores zijn waardevol, maar belastbaarheid gaat over herhaald kunnen leveren. Blijf opbouwen met aandacht voor bepakking, herstel, ondergrond, volume en signalen van je lichaam.",
    score: null,
    maxScore: null,
  },
  {
    id: "minimumStandards",
    title: "Ondergrens",
    text: "Minimale eisen zijn geen eindpunt. Ze zijn de ondergrens waarop je liever niet wilt verschijnen. Hoe meer marge je opbouwt, hoe beter je lichaam de opleiding en belasting daarna kan verwerken.",
    score: null,
    maxScore: null,
  },
];

const specialistNuanceRules = [
  {
    questionId: "loadCarriage",
    value: "C",
    title: "Bepakking",
    text: `Goed dat je al structureel kilometers maakt met bepakking. De opbouw moet passen bij je belastbaarheid en herstel. ${getOfficialRequirementText("cluster6", "march")} Dit protocol hoort bij de functionele aanstellingskeuring, niet bij de aanvullende Mariniers- of KCT-test. De testgewichten zijn geen startadvies; bouw je trainingsbelasting geleidelijk op.`,
  },
  {
    questionId: "pullups",
    value: "C",
    title: "Trekkracht",
    text: `Vijf of meer strikte pull-ups is een Mission Movement startreferentie, geen officiële selectie-eis. ${getOfficialRequirementText("kct", "pullups")} Dit geldt voor de informatiedagen, niet als algemene Cluster 6-eis. Meer trekkracht kan een trainingsdoel zijn, maar is daarmee geen hoger officieel minimum.`,
  },
  {
    questionId: "run12",
    value: "C",
    title: "Hardlopen",
    text: `Je loopscore geeft informatie over je voorbereiding, maar is geen uitslag van een officiële test. ${getOfficialRequirementText("kct", "run")} Dit is een ander testmoment dan de functionele Cluster 6-keuring. Richting 3000 meter of meer trainen kan een eigen voorbereidingsdoel zijn; het is geen extra officiële eis.`,
  },
];

const painNuance = {
  questionId: "pain",
  value: "C",
  title: "Blessurevrij blijven",
  text: "Goed dat je momenteel zonder duidelijke klachten traint. Het doel is nu om dat zo te houden terwijl de belasting omhooggaat. Blessurevrij zijn betekent niet dat je onbeperkt kunt stapelen. Het betekent dat je ruimte hebt om progressief te bouwen aan belastbaarheid: meer volume, meer herhaling, meer bepakking en meer intensiteit, zonder je lichaam onnodig te slopen.",
};

const getQuestion = (questionId) =>
  questions.find((question) => question.id === questionId);

const getAnswerScore = (answers, questionId) => {
  const question = getQuestion(questionId);
  const selectedValue = answers[questionId];
  const selectedOption = question?.options.find(
    (option) => option.value === selectedValue,
  );

  return selectedOption?.score ?? 0;
};

const getQuestionMaxScore = (questionId) => {
  const question = getQuestion(questionId);

  return Math.max(...(question?.options.map((option) => option.score) ?? [0]));
};

const getDirectionLabel = (answers) => {
  const directionQuestion = getQuestion("direction");
  const selectedValue = answers.direction;

  return (
    directionQuestion?.options.find((option) => option.value === selectedValue)
      ?.label ?? "jouw richting"
  );
};

const getNuanceBlocks = (answers, isSpecialistDirection) => {
  const rules = isSpecialistDirection
    ? [...specialistNuanceRules, painNuance]
    : [painNuance];

  return rules
    .filter((rule) => answers[rule.questionId] === rule.value)
    .slice(0, 3)
    .map(({ title, text }) => ({ title, text }));
};

const isValidEmail = (value) => {
  const trimmedValue = value.trim();

  return trimmedValue.includes("@") && trimmedValue.includes(".");
};

const formCardClassName =
  "rounded-lg border border-yellow-500/20 bg-[#10100c] p-5 shadow-[0_18px_55px_-36px_rgba(255,204,1,0.55)] md:p-7";

const PreparationCheckForm = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const currentQuestion = questions[currentQuestionIndex];
  const progressPercentage =
    ((currentQuestionIndex + 1) / questions.length) * 100;

  useEffect(() => {
    trackPreparationCheckView();
  }, []);

  const result = useMemo(() => {
    const scoredQuestionIds = questions
      .map((question) => question.id)
      .filter((questionId) => questionId !== "direction");

    const totalScore = scoredQuestionIds.reduce(
      (sum, questionId) => sum + getAnswerScore(answers, questionId),
      0,
    );

    const isSpecialistDirection = answers.direction === "B";
    const isSafetyDirection = answers.direction === "C";
    const resultType =
      totalScore <= 6
        ? "low"
        : totalScore <= 11
          ? "mid"
          : isSpecialistDirection
            ? "highSpecialist"
            : isSafetyDirection
              ? "highSafety"
              : "highGeneral";

    const domainScores = domains
      .map((domain) => {
        const score = domain.questionIds.reduce(
          (sum, questionId) => sum + getAnswerScore(answers, questionId),
          0,
        );
        const maxScore = domain.questionIds.reduce(
          (sum, questionId) => sum + getQuestionMaxScore(questionId),
          0,
        );

        return {
          ...domain,
          score,
          maxScore,
          ratio: maxScore > 0 ? score / maxScore : 1,
        };
      })
      .sort((a, b) => a.ratio - b.ratio);

    return {
      totalScore,
      resultType,
      resultHeading: resultContent[resultType].title,
      resultText: resultContent[resultType].text,
      focusAreas:
        totalScore >= 12 ? highScoreFocusAreas : domainScores.slice(0, 3),
      nuanceBlocks: getNuanceBlocks(answers, isSpecialistDirection),
      domainScores,
      directionLabel: getDirectionLabel(answers),
    };
  }, [answers]);

  const emailHasValue = email.trim().length > 0;
  const emailIsValid = !emailHasValue || isValidEmail(email);
  const canSubmitEmail =
    emailHasValue &&
    emailIsValid &&
    consent &&
    submitStatus !== "loading" &&
    submitStatus !== "success";

  const buildAnswerPayload = () =>
    questions.reduce((payload, question) => {
      const selectedValue = answers[question.id];
      const selectedOption = question.options.find(
        (option) => option.value === selectedValue,
      );

      if (!selectedOption) return payload;

      return {
        ...payload,
        [question.id]: {
          value: selectedOption.value,
          label: selectedOption.label,
          score: selectedOption.score,
        },
      };
    }, {});

  const handleAnswerSelect = (questionId, value) => {
    setSubmitMessage("");
    setSubmitStatus("idle");
    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [questionId]: value,
    }));

    if (currentQuestionIndex === questions.length - 1) {
      setShowResult(true);
      return;
    }

    setCurrentQuestionIndex((previousIndex) => previousIndex + 1);
  };

  const handlePrevious = () => {
    setSubmitMessage("");
    setSubmitStatus("idle");
    setCurrentQuestionIndex((previousIndex) => Math.max(previousIndex - 1, 0));
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    if (!emailHasValue || !consent) return;

    if (!isValidEmail(email)) {
      setSubmitStatus("error");
      setSubmitMessage("Vul een geldig e-mailadres in.");
      return;
    }

    setSubmitStatus("loading");
    setSubmitMessage("");

    const answerPayload = buildAnswerPayload();
    const focusAreas = result.focusAreas.map((domain) => ({
      id: domain.id,
      title: domain.title,
      text: domain.text,
      score: domain.score,
      maxScore: domain.maxScore,
    }));

    try {
      const response = await fetch(PREPARATION_CHECK_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          consent,
          company,
          answers: answerPayload,
          scores: {
            total: result.totalScore,
            domains: result.domainScores.map((domain) => ({
              id: domain.id,
              title: domain.title,
              score: domain.score,
              maxScore: domain.maxScore,
            })),
          },
          resultType: result.resultType,
          resultTitle: result.resultHeading,
          focusAreas,
          nuanceBlocks: result.nuanceBlocks,
          direction: result.directionLabel,
          createdAtClient: new Date().toISOString(),
          source: "preparation-check",
        }),
      });

      if (!response.ok) {
        throw new Error("Preparation check submit failed");
      }

      setSubmitStatus("success");
      setSubmitMessage("Je analyse is verstuurd. Check je inbox.");
      trackPreparationCheckLead({
        direction: result.directionLabel,
        resultType: result.resultType,
        resultTitle: result.resultHeading,
      });
    } catch {
      setSubmitStatus("error");
      setSubmitMessage(
        "Er ging iets mis bij het versturen. Probeer het later opnieuw of stuur Mission Movement een bericht.",
      );
    }
  };

  const resetCheck = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResult(false);
    setEmail("");
    setConsent(false);
    setCompany("");
    setSubmitStatus("idle");
    setSubmitMessage("");
  };

  if (showResult) {
    return (
      <form
        className={formCardClassName}
        aria-labelledby="preparation-check-result-title"
        onSubmit={handleEmailSubmit}
      >
        <div className="mb-6 h-1 w-16 rounded-full bg-mmAccent" />
        <div className="space-y-6">
          <div>
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-xs md:text-sm mb-3">
              Resultaat
            </p>
            <h3
              id="preparation-check-result-title"
              className="font-display uppercase tracking-widest text-white text-2xl md:text-3xl leading-none"
            >
              {result.resultHeading}
            </h3>
          </div>

          <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
            Je check is afgerond. Op basis van je antwoorden is er een analyse
            gemaakt van je fysieke basis, belastbaarheid, trainingsstructuur en
            richting-specifieke voorbereiding.
          </p>

          <div className="rounded-lg border border-mmAccent/30 bg-mmAccent/10 p-4">
            <p className="text-sm text-mmText leading-relaxed">
              Je volledige analyse met aandachtspunten en volgende stappen
              ontvang je per e-mail.
            </p>
          </div>

          <div className="space-y-4 border-t border-mmBorder pt-5">
            <div>
              <h4 className="font-display uppercase tracking-widest text-white text-lg mb-2">
                Ontvang je volledige analyse
              </h4>
              <p className="text-sm text-mmTextMuted leading-relaxed">
                Vul je e-mailadres in om je volledige analyse en
                aandachtspunten te ontvangen.
              </p>
            </div>

            <div>
              <label
                htmlFor="preparation-check-email"
                className="mb-2 block text-sm font-semibold text-white"
              >
                E-mailadres
              </label>
              <input
                id="preparation-check-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setSubmitStatus("idle");
                  setSubmitMessage("");
                }}
                placeholder="jij@example.com"
                aria-invalid={emailHasValue && !emailIsValid}
                aria-describedby={
                  emailHasValue && !emailIsValid
                    ? "preparation-check-email-error"
                    : undefined
                }
                className="w-full rounded-lg border border-mmBorder bg-black/30 px-4 py-3 text-base text-white placeholder:text-mmTextMuted outline-none transition focus-visible:border-mmAccent focus-visible:ring-2 focus-visible:ring-mmFocus"
              />
              {emailHasValue && !emailIsValid && (
                <p
                  id="preparation-check-email-error"
                  className="mt-2 text-sm text-red-300"
                >
                  Vul een geldig e-mailadres in.
                </p>
              )}
            </div>

            <div className="hidden" aria-hidden="true">
              <label htmlFor="preparation-check-company">Bedrijf</label>
              <input
                id="preparation-check-company"
                type="text"
                name="company"
                tabIndex="-1"
                autoComplete="off"
                value={company}
                onChange={(event) => setCompany(event.target.value)}
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-mmTextMuted leading-relaxed">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => {
                  setConsent(event.target.checked);
                  setSubmitStatus("idle");
                  setSubmitMessage("");
                }}
                className="mt-1 h-4 w-4 shrink-0 accent-mmAccent focus-visible:ring-2 focus-visible:ring-mmFocus"
              />
              <span>
                Ik geef toestemming om mijn resultaat per e-mail te ontvangen
                en begrijp dat Mission Movement mij aanvullende informatie over
                voorbereiding kan sturen.
              </span>
            </label>

            <p className="text-xs text-mmTextMuted leading-relaxed">
              Je kunt je later altijd uitschrijven. Deze check vervangt geen
              medische beoordeling of officiële selectie-eis.
            </p>

            <button
              type="submit"
              className="mm-btnPrimary w-full items-center justify-center gap-3 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canSubmitEmail}
              aria-busy={submitStatus === "loading"}
            >
              {submitStatus === "loading" && (
                <span
                  className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black"
                  aria-hidden="true"
                />
              )}
              <span>
                {submitStatus === "loading"
                  ? "Analyse wordt verstuurd..."
                  : "Stuur mijn analyse per e-mail"}
              </span>
            </button>

            {submitStatus === "loading" && (
              <p className="text-sm text-mmTextMuted">
                Dit kan een paar seconden duren.
              </p>
            )}

            {submitMessage && (
              <p
                className={`rounded-lg border px-4 py-3 text-sm ${
                  submitStatus === "error"
                    ? "border-red-400/40 bg-red-950/30 text-red-100"
                    : "border-mmAccent/30 bg-mmAccent/10 text-mmText"
                }`}
              >
                {submitMessage}
                {submitStatus === "success" && (
                  <span className="mt-1 block text-xs text-mmTextMuted">
                    De volledige analyse is naar je e-mail gestuurd.
                  </span>
                )}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={resetCheck}
            className="text-sm font-semibold text-mmAccent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mmFocus"
          >
            Check opnieuw doen
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className={formCardClassName}
      aria-labelledby="preparation-check-form-title"
    >
      <div className="mb-6 h-1 w-16 rounded-full bg-mmAccent" />
      <div className="space-y-6">
        <div>
          <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-xs md:text-sm mb-3">
            Gratis voorbereidings-check
          </p>
          <h3
            id="preparation-check-form-title"
            className="font-display uppercase tracking-widest text-white text-2xl md:text-3xl leading-none"
          >
            {currentQuestion.question}
          </h3>
        </div>

        <div className="space-y-3">
          {currentQuestion.options.map((option) => {
            const isSelected = answers[currentQuestion.id] === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  handleAnswerSelect(currentQuestion.id, option.value)
                }
                className={`w-full rounded-lg border px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mmFocus ${
                  isSelected
                    ? "border-mmAccent bg-mmAccent/10 text-white"
                    : "border-mmBorder bg-black/20 text-mmTextMuted hover:border-mmAccent/60 hover:bg-black/30 hover:text-white"
                }`}
              >
                <span className="flex items-start gap-3">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-bold ${
                      isSelected
                        ? "border-mmAccent bg-mmAccent text-black"
                        : "border-mmBorderStrong text-mmAccent"
                    }`}
                  >
                    {option.value}
                  </span>
                  <span className="pt-0.5 text-sm md:text-base leading-relaxed">
                    {option.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="space-y-4 border-t border-mmBorder pt-5">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={handlePrevious}
              className="rounded-lg border border-mmBorder px-4 py-2 text-sm font-semibold text-mmTextMuted transition hover:border-mmAccent/60 hover:text-mmAccent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mmFocus disabled:cursor-not-allowed disabled:opacity-40"
              disabled={currentQuestionIndex === 0}
            >
              Vorige
            </button>

            <p className="text-sm font-semibold text-mmTextMuted">
              Vraag {currentQuestionIndex + 1} van {questions.length}
            </p>
          </div>

          <div
            className="h-2 overflow-hidden rounded-full bg-black/40"
            aria-label={`Voortgang: vraag ${currentQuestionIndex + 1} van ${
              questions.length
            }`}
            role="progressbar"
            aria-valuenow={currentQuestionIndex + 1}
            aria-valuemin="1"
            aria-valuemax={questions.length}
          >
            <div
              className="h-full rounded-full bg-mmAccent transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default PreparationCheckForm;
